import { ICON_PATHS, ICON_VIEWBOX, type IconName } from './icons.generated'

export type { IconName }

type Props = {
  name: IconName
  /** Pixel size; icons are square. */
  size?: number
  /**
   * Accessible name. Omit ONLY when the icon is decorative and an adjacent element
   * already names the control. The existing UI labels every icon button
   * (`aria-label="Clear search"` etc.) and swapping a text glyph for an icon must
   * not drop that — see docs/UI-COMPONENTS.md §4.2.
   */
  label?: string
  className?: string
}

/**
 * The single place icon rendering is defined. Style (outlined/rounded/sharp), size
 * and colour all flow through here, so switching the whole set is one change to
 * icons.generated.ts rather than a sweep through the app.
 *
 * Colour comes from `currentColor`, so icons inherit text colour and the light/dark
 * flip needs no per-icon work.
 */
export function Icon({ name, size = 20, label, className }: Props) {
  const paths = ICON_PATHS[name]
  return (
    <svg
      viewBox={ICON_VIEWBOX}
      width={size}
      height={size}
      className={className}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      {paths.map((d) => (
        <path key={d} fill="currentColor" d={d} />
      ))}
    </svg>
  )
}
